import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import MasterLayout from '../../components/layouts/masterlayout';
import ErrorMessage from '../../components/common/errormessage';
import SuccessMessage from '../../components/common/successmessage';
import GeneralTextBox from '../common/textboxes/textbox_general';
import GeneralButton from '../common/button_general';
import GeneralDropdown from '../../components/common/dropdowns/dropdown_general';
import { getHeaders, handleResponse, handleErrorResponse } from '../../lib/requests';
import Spinner from '../common/spinner';
import MenuItems from './menuitems';

function MenuForm({ menu = {} }) {
  const { menuId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    menu_id: menuId || '',
    name: menu.name || '',
    reference_id: menu.reference_id || '',
    menu_type: menu.menu_type || 'Standard',
  });

  const [menuItems, setMenuItems] = useState([]);
  const [allItems, setAllItems] = useState([]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setShowError(false);
    setShowSuccess(false);
  };

  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;
        setIsLoading(true);
        
        const menuResponse = await axios.get(`${apiUrl}/menus/${menuId}`, {
          headers: getHeaders(apiToken),
        });
        const menuData = menuResponse.data.data;
        setFormData({
          name: menuData.name || '',
          reference_id: menuData.reference_id || '',
          menu_type: menuData.menu_type || 'Standard',
        });
        
        const menuItemsResponse = await axios.get(`${apiUrl}/menu_items/${menuId}`, {
          headers: getHeaders(apiToken),
        });
        setMenuItems(menuItemsResponse.data.data);

        const itemsResponse = await axios.get(`${apiUrl}/items?pageSize=1000`, {
          headers: getHeaders(apiToken),
        });
        setAllItems(itemsResponse.data.data);
        
        setShowError(false);
      } catch (error) {
        handleErrorResponse(error, navigate, setShowError, setErrorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (menuId) {
      fetchMenuData();
    }
  }, [menuId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const apiToken = localStorage.getItem('accessToken') || '';
    const apiUrl = process.env.REACT_APP_API_URL || '';

    resetForm();

    if (!apiToken || !apiUrl) {
      console.error('API token or URL is missing');
      setShowError(true);
      setErrorMessage('Configuration error');
      return;
    }

    const headers = getHeaders(apiToken);
    const url = menuId ? `${apiUrl}/menus/${menuId}` : `${apiUrl}/menus`;
    const method = menuId ? 'put' : 'post';

    const payload = { ...formData };

    try {
      const response = await axios({
        method,
        url,
        data: JSON.stringify(payload),
        headers,
      });

      handleResponse(
        response,
        'Menu',
        setShowSuccess,
        setSuccessMessage,
        setShowError,
        setErrorMessage,
        navigate,
        '/menus'
      );
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    }
  };

  const handleMenuItemChange = async (index, field, value) => {
    const updatedMenuItems = [...menuItems];
    updatedMenuItems[index][field] = value;
  
    updatedMenuItems[index].loading = true;
    setMenuItems(updatedMenuItems);
  
    if (field === 'item_id' && value) {
      try {
        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;
  
        const menuItemPayload = {
          menu_id: menuId,
          menu_item_id: updatedMenuItems[index].menu_item_id || null,
          item_id: value,
          sort_order: updatedMenuItems[index].sort_order,
        };
  
        let newMenuItemId = updatedMenuItems[index].menu_item_id;
  
        if (!menuItemPayload.menu_item_id) {
          const response = await axios.post(
            `${apiUrl}/menu_items`,
            menuItemPayload,
            { headers: getHeaders(apiToken) }
          );
          newMenuItemId = response.data.data.menu_item_id;
        } else {
          await axios.put(
            `${apiUrl}/menu_items/${menuItemPayload.menu_item_id}`,
            { item_id: value, sort_order: menuItemPayload.sort_order },
            { headers: getHeaders(apiToken) }
          );
        }
  
        const newMenuItems = updatedMenuItems.map((item, i) =>
          i === index
            ? { ...item, menu_item_id: newMenuItemId, loading: false }
            : item
        );
        setMenuItems(newMenuItems);
      } catch (error) {
        console.error('Failed to add/update menu item', error);
        setShowError(true);
        setErrorMessage('Failed to save menu item');
      }
    } else {
      updatedMenuItems[index].loading = false;
      setMenuItems(updatedMenuItems);
    }
  };

  const addMenuItem = () => {
    setMenuItems([...menuItems, { menu_item_id: null, item_id: null, sort_order: menuItems.length + 1 }]);
  };

  const removeMenuItem = async (index, menuItemId) => {
    const updatedMenuItems = [...menuItems];
    
    if (menuItemId) {
      try {
        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;
        await axios.delete(`${apiUrl}/menu_items/${menuItemId}`, { headers: getHeaders(apiToken) });
      } catch (error) {
        console.error('Failed to delete menu item', error);
        return;
      }
    }

    updatedMenuItems.splice(index, 1);
    setMenuItems(updatedMenuItems);
  };

  const reorderMenuItem = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= menuItems.length) return;
    const reorderedMenuItems = [...menuItems];
    const [movedItem] = reorderedMenuItems.splice(fromIndex, 1);
    reorderedMenuItems.splice(toIndex, 0, movedItem);
    setMenuItems(reorderedMenuItems.map((item, i) => ({ ...item, sort_order: i + 1 })));
  };

  const handleBack = () => navigate('/menus');

  const pageTitle = menuId ? `Edit ${formData.name}` : 'Create Menu';

  return (
    <MasterLayout pageTitle={pageTitle}>
      {showError && (
        <ErrorMessage
          message={errorMessage}
          linkText=""
          linkHref="/retry-url"
        />
      )}
      {showSuccess && (
        <SuccessMessage message={successMessage} setShowSuccess={setShowSuccess} />
      )}
      {isLoading && <Spinner />}
      {!isLoading && (
        <form onSubmit={handleSubmit} className="p-4">
          <div className="grid gap-6 mb-6 md:grid-cols-3">
            <GeneralTextBox
              label="Name"
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Menu Name"
              required={true}
            />
            <GeneralTextBox
              label="Reference ID"
              id="reference_id"
              name="reference_id"
              type="text"
              value={formData.reference_id}
              onChange={handleChange}
              placeholder="Reference ID"
            />
            <GeneralDropdown
              label="Menu Price Category"
              id="menu_type"
              name="menu_type"
              value={formData.menu_type}
              onChange={handleChange}
              options={[
                { value: 'Standard', name: 'Standard' },
                { value: 'Therapeutic', name: 'Therapeutic' }
              ]}
              placeholder="Select Menu Price Category"
              required={true}
            />
          </div>
          
          <MenuItems
            items={allItems}
            menuItems={menuItems}
            handleItemChange={handleMenuItemChange}
            addItem={addMenuItem}
            removeItem={removeMenuItem}
            reorderItem={reorderMenuItem}
            disabled={false}
          />
          
          <div className="flex justify-center mt-6">
            <GeneralButton type="submit" buttonType="primary">
              {menuId ? 'Update Menu' : 'Create Menu'}
            </GeneralButton>
            <GeneralButton type="button" buttonType="secondary" onClick={handleBack}>
              Back to Menu List
            </GeneralButton>
          </div>
        </form>
      )}
    </MasterLayout>
  );
}

MenuForm.propTypes = {
  menu: PropTypes.shape({
    name: PropTypes.string,
    reference_id: PropTypes.string,
    menu_type: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  }),
};

export default MenuForm;
